import React from 'react';
import {
  level1Options,
  level2Options,
  level3Options,
  type SalesLevelDrill,
  type SalesLevelPair,
} from '../../../../support/campaignSalesLevels';
import { DrillBar, DrillSelect } from './dashboardParts';

/** Sentinel value for the "no filter" option — kept distinct from any real territory name. */
const ALL = '';

/**
 * The Global → Sales Level 1 → Sales Level 2 → Sales Level 3 drill control. A single Sales-Level-1
 * select starts at "All territories" (aggregate everything); picking a level-1 filters the surrounding
 * charts AND reveals a second Sales-Level-2 select scoped to that level-1's sub-regions; picking a
 * level-2 in turn reveals a third Sales-Level-3 select. Clearing a level resets the levels below it.
 * Options are derived from the population actually in scope (via
 * {@link level1Options}/{@link level2Options}/{@link level3Options}), so only territories with data appear.
 */
interface Props {
  /** Every sales-level pair in the current population — drives the available options. */
  pairs: SalesLevelPair[];
  drill: SalesLevelDrill;
  onChange: (drill: SalesLevelDrill) => void;
  /** Label for the level-1 dimension (default "Sales Level 1"). */
  level1Label?: string;
  /** Label for the level-2 dimension (default "Sales Level 2"). */
  level2Label?: string;
  /** Label for the level-3 dimension (default "Sales Level 3"). */
  level3Label?: string;
}

const SalesLevelDrillSelect: React.FC<Props> = ({
  pairs,
  drill,
  onChange,
  level1Label = 'Sales Level 1',
  level2Label = 'Sales Level 2',
  level3Label = 'Sales Level 3',
}) => {
  const l1 = React.useMemo(() => level1Options(pairs), [pairs]);
  const l2 = React.useMemo(
    () => (drill.sl1 ? level2Options(pairs, drill.sl1) : []),
    [pairs, drill.sl1]
  );
  const l3 = React.useMemo(
    () => (drill.sl1 && drill.sl2 ? level3Options(pairs, drill.sl1, drill.sl2) : []),
    [pairs, drill.sl1, drill.sl2]
  );

  const level1Opts = React.useMemo(
    // "All territories" = the no-filter aggregate (kept distinct from the "Global" catch-all bucket,
    // which is a real level-1 value covering theatre-only / unassigned territories).
    () => [{ value: ALL, label: 'All territories' }, ...l1.map((v) => ({ value: v, label: v }))],
    [l1]
  );
  const level2Opts = React.useMemo(
    () => [{ value: ALL, label: `All of ${drill.sl1}` }, ...l2.map((v) => ({ value: v, label: v }))],
    [l2, drill.sl1]
  );
  const level3Opts = React.useMemo(
    () => [{ value: ALL, label: `All of ${drill.sl2}` }, ...l3.map((v) => ({ value: v, label: v }))],
    [l3, drill.sl2]
  );

  return (
    <DrillBar>
      <DrillSelect
        label={level1Label}
        value={drill.sl1 ?? ALL}
        options={level1Opts}
        onChange={(v) => onChange(v ? { sl1: v } : {})}
      />
      {drill.sl1 && (
        <DrillSelect
          label={level2Label}
          value={drill.sl2 ?? ALL}
          options={level2Opts}
          onChange={(v) => onChange(v ? { sl1: drill.sl1, sl2: v } : { sl1: drill.sl1 })}
        />
      )}
      {drill.sl1 && drill.sl2 && (
        <DrillSelect
          label={level3Label}
          value={drill.sl3 ?? ALL}
          options={level3Opts}
          onChange={(v) =>
            onChange(
              v ? { sl1: drill.sl1, sl2: drill.sl2, sl3: v } : { sl1: drill.sl1, sl2: drill.sl2 }
            )
          }
        />
      )}
    </DrillBar>
  );
};

export default SalesLevelDrillSelect;
